"use client";

import { createContext, useContext } from "react";

import { useLomdaV3Editor } from "../store/useLomdaV3Editor";

/**
 * Whether a block should render its editor (true) or its read-only preview
 * (false). On the canvas only text blocks are editable inline; everything else
 * renders as preview and edits inside a modal/bottom-sheet host (which provides
 * editable=true). Falls back to the store mode when no provider is present.
 */
const BlockEditableContext = createContext<boolean | null>(null);

export function BlockEditableProvider({
    editable,
    children,
}: {
    editable: boolean;
    children: React.ReactNode;
}) {
    return (
        <BlockEditableContext.Provider value={editable}>
            {children}
        </BlockEditableContext.Provider>
    );
}

export function useBlockEditable(): boolean {
    const ctx = useContext(BlockEditableContext);
    const mode = useLomdaV3Editor((s) => s.mode);
    return ctx ?? mode === "edit";
}

/**
 * True when blocks render inside a page thumbnail. Thumbnails render dozens of
 * blocks scaled-down, so they must be cheap + deterministic: text renders as
 * static HTML (no TipTap mount) and embeds/videos as placeholders (no live
 * iframes) — otherwise async mounts make the thumbnail flicker to the wrong
 * block before it settles.
 */
const BlockThumbnailContext = createContext<boolean>(false);

export function BlockThumbnailProvider({
    children,
}: {
    children: React.ReactNode;
}) {
    return (
        <BlockThumbnailContext.Provider value={true}>
            {children}
        </BlockThumbnailContext.Provider>
    );
}

export function useBlockThumbnail(): boolean {
    return useContext(BlockThumbnailContext);
}
